import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { collection, doc, getDoc, setDoc, deleteDoc } from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL, deleteObject } from 'firebase/storage';
import { db, storage, auth } from '../../firebase';
import { onAuthStateChanged } from 'firebase/auth';
import FormatBar from '../format_bar/formatBar';
import './adminNyheder.css';

const AdminNyheder = () => {
  const navigate = useNavigate();
  const editorRef = useRef(null);
  
  // Tilføj MutationObserver ref
  const contentObserver = useRef(null);

  // Auth state
  const [user, setUser] = useState(null);
  const [isLoading, setIsLoading] = useState(true);

  // Nyheder states
  const [nyheder, setNyheder] = useState([]);
  const [isEditing, setIsEditing] = useState(false);
  const [isCreating, setIsCreating] = useState(false);
  const [selectedNyhed, setSelectedNyhed] = useState(null);
  const [editedNyhed, setEditedNyhed] = useState({
    id: '',
    title: '',
    content: '',
    buttonText: 'Læs mere',
    buttonLink: '/',
    image: null,
    imageUrl: ''
  });
  
  // UI states
  const [message, setMessage] = useState({ type: '', text: '' });
  const [selectedFile, setSelectedFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [uploading, setUploading] = useState(false);

  // Tilføj format states
  const [activeFormats, setActiveFormats] = useState({
    bold: false,
    italic: false,
    heading: false,
    bullet: false,
    link: false
  });

  // Check auth
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (user) => {
      setUser(user);
      setIsLoading(false);
      if (!user) {
        navigate('/admin/login');
      }
    });

    return () => unsubscribe();
  }, [navigate]);

  // Load nyheder
  useEffect(() => {
    const fetchNyheder = async () => {
      try {
        const nyhedDocRef = doc(db, 'content', 'nyheder');
        const nyhedDoc = await getDoc(nyhedDocRef);
        
        if (nyhedDoc.exists()) {
          // Sorter nyheder med de nyeste først (baseret på ID timestamp)
          const sortedNyheder = [...(nyhedDoc.data().items || [])].sort((a, b) => {
            const aId = a.id.includes('_') ? parseInt(a.id.split('_')[1]) : 0;
            const bId = b.id.includes('_') ? parseInt(b.id.split('_')[1]) : 0;
            return bId - aId; // Faldende sortring (nyeste først)
          });
          
          setNyheder(sortedNyheder);
        }
      } catch (error) {
        console.error('Fejl ved hentning af nyheder:', error);
        setMessage({
          text: 'Der opstod en fejl ved indlæsning af nyheder',
          type: 'error'
        });
      }
    };

    if (user) {
      fetchNyheder();
    }
  }, [user]);

  // Setup observer når komponenten mountes
  useEffect(() => {
    const observerConfig = {
      childList: true,
      characterData: true,
      subtree: true
    };

    contentObserver.current = new MutationObserver(() => {
      if (editorRef.current) {
        setEditedNyhed(prev => ({
          ...prev,
          content: editorRef.current.innerHTML
        }));
      }
    });

    return () => {
      contentObserver.current?.disconnect();
    };
  }, []);

  // Start observation når redigering begynder
  useEffect(() => {
    if (isEditing || isCreating) {
      if (editorRef.current) {
        contentObserver.current?.observe(editorRef.current, {
          childList: true,
          characterData: true,
          subtree: true
        });
      }
    }

    return () => {
      contentObserver.current?.disconnect();
    };
  }, [isEditing, isCreating]);

  // Tilføj selection change listener
  useEffect(() => {
    const handleSelectionChange = () => {
      if (!isEditing && !isCreating) return;
      
      const activeElement = document.activeElement;
      if (editorRef.current && editorRef.current.contains(activeElement)) {
        checkActiveFormats();
      }
    };

    document.addEventListener('selectionchange', handleSelectionChange);
    return () => {
      document.removeEventListener('selectionchange', handleSelectionChange);
    };
  }, [isEditing, isCreating]);

  const handleEditorFocus = () => {
    if (editorRef.current) {
      editorRef.current.focus();
      checkActiveFormats();
    }
  };

  const checkActiveFormats = () => {
    setActiveFormats({
      bold: document.queryCommandState('bold'),
      italic: document.queryCommandState('italic'),
      heading: false,
      bullet: document.queryCommandState('insertUnorderedList'),
      link: document.queryCommandState('createLink')
    });
  };

  const handleFormatClick = (formatType) => {
    if (!editorRef.current) return;
    
    if (!editorRef.current.contains(document.activeElement)) {
      editorRef.current.focus();
    }

    const selection = window.getSelection();
    if (!selection.rangeCount) return;
    
    const range = selection.getRangeAt(0);
    const selectedText = range.toString();
    
    if (!editorRef.current.contains(range.commonAncestorContainer)) {
      return;
    }

    switch (formatType) {
      case 'bold':
        document.execCommand('bold', false);
        break;
      
      case 'italic':
        document.execCommand('italic', false);
        break;
      
      case 'heading':
        const parentElement = range.commonAncestorContainer.parentElement;
        const isHeading = parentElement.tagName === 'H2';
        
        if (isHeading) {
          document.execCommand('formatBlock', false, 'p');
        } else {
          document.execCommand('formatBlock', false, 'h2');
        }
        break;
      
      case 'bullet':
        document.execCommand('insertUnorderedList', false);
        break;
      
      case 'link':
        const url = selectedText.trim();
        if (url) {
          const validUrl = isValidUrl(url);
          if (validUrl) {
            document.execCommand('createLink', false, validUrl);
          } else {
            const linkUrl = prompt('Indtast URL:', 'https://');
            if (linkUrl && linkUrl.trim() !== '') {
              document.execCommand('createLink', false, linkUrl);
            }
          }
        }
        break;
      
      default:
        break;
    }

    checkActiveFormats();
  };

  const isValidUrl = (text) => {
    try {
      if (text.match(/^(https?:\/\/|www\.)/i)) {
        const url = text.startsWith('www.') ? `https://${text}` : text;
        new URL(url);
        return url;
      }
      return false;
    } catch {
      return false;
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      document.execCommand('insertLineBreak', false);
    }
  };

  const handleFileChange = (e) => {
    if (e.target.files[0]) {
      const file = e.target.files[0];
      setSelectedFile(file);
      
      const reader = new FileReader();
      reader.onloadend = () => {
        setPreview(reader.result);
      };
      reader.readAsDataURL(file);

      setEditedNyhed(prev => ({
        ...prev,
        image: file.name
      }));
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setEditedNyhed(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleCreateNyhed = () => {
    setIsCreating(true);
    setIsEditing(false);
    setSelectedNyhed(null);
    setEditedNyhed({
      id: `nyhed_${Date.now()}`,
      title: '',
      content: '',
      buttonText: 'Læs mere',
      buttonLink: '/',
      image: null,
      imageUrl: ''
    });
    setSelectedFile(null);
    setPreview(null);
    
    // Nulstil editor indhold
    setTimeout(() => {
      if (editorRef.current) {
        editorRef.current.innerHTML = '';
      }
    }, 0);
  };

  const handleEditNyhed = (nyhed) => {
    setIsEditing(true);
    setIsCreating(false);
    setSelectedNyhed(nyhed);
    setEditedNyhed({
      ...nyhed
    });
    setSelectedFile(null);
    setPreview(nyhed.imageUrl || null);
    
    // Initialiser editor indhold
    setTimeout(() => {
      if (editorRef.current) {
        editorRef.current.innerHTML = nyhed.content || '';
      }
    }, 0);
  };

  const handleDeleteNyhed = async (nyhedId) => {
    if (window.confirm('Er du sikker på, at du vil slette denne nyhed?')) {
      try {
        // Find nyhed for at få billedet
        const nyhedToDelete = nyheder.find(n => n.id === nyhedId);
        
        // Slet billede hvis det findes
        if (nyhedToDelete.image) {
          try {
            const imageRef = ref(storage, `nyheder/${nyhedToDelete.image}`);
            await deleteObject(imageRef);
          } catch (imgError) {
            console.error("Fejl ved sletning af billede:", imgError);
            // Fortsæt alligevel for at slette nyhed
          }
        }
        
        // Opdater nyheder array
        const updatedNyheder = nyheder.filter(nyhed => nyhed.id !== nyhedId);
        setNyheder(updatedNyheder);
        
        // Gem opdateret array i Firestore
        await setDoc(doc(db, 'content', 'nyheder'), {
          items: updatedNyheder,
          lastUpdated: new Date()
        });
        
        setMessage({ type: 'success', text: 'Nyhed blev slettet!' });
      } catch (error) {
        console.error("Fejl ved sletning:", error);
        setMessage({ type: 'error', text: 'Fejl ved sletning af nyhed.' });
      }
    }
  };

  const handleSaveNyhed = async () => {
    try {
      setUploading(true);
      let imageUrl = editedNyhed.imageUrl || '';
      
      // Upload nyt billede hvis valgt
      if (selectedFile) {
        const storageRef = ref(storage, `nyheder/${selectedFile.name}`);
        await uploadBytes(storageRef, selectedFile);
        imageUrl = await getDownloadURL(storageRef);
      }
      
      const nyhedToSave = {
        ...editedNyhed,
        imageUrl: imageUrl
      };
      
      let updatedNyheder = [];
      
      if (isCreating) {
        // Tilføj ny nyhed
        updatedNyheder = [...nyheder, nyhedToSave];
      } else {
        // Opdater eksisterende nyhed
        updatedNyheder = nyheder.map(nyhed => 
          nyhed.id === editedNyhed.id ? nyhedToSave : nyhed
        );
      }
      
      setNyheder(updatedNyheder);
      
      // Gem opdateret array i Firestore
      await setDoc(doc(db, 'content', 'nyheder'), {
        items: updatedNyheder,
        lastUpdated: new Date()
      });
      
      setMessage({ 
        type: 'success', 
        text: isCreating ? 'Nyhed blev oprettet!' : 'Nyhed blev opdateret!' 
      });
      
      // Reset form state
      setIsEditing(false);
      setIsCreating(false);
      setSelectedNyhed(null);
      setSelectedFile(null);
      setPreview(null);
    } catch (error) {
      console.error("Fejl ved gem:", error);
      setMessage({ type: 'error', text: 'Fejl ved gem af nyhed.' });
    } finally {
      setUploading(false);
    }
  };

  const handleCancel = () => {
    setIsEditing(false);
    setIsCreating(false);
    setSelectedNyhed(null);
    setSelectedFile(null);
    setPreview(null);
  };

  const closeMessage = () => {
    setMessage({ type: '', text: '' });
  };

  const goBack = () => {
    navigate('/admin');
  };

  if (isLoading) {
    return (
      <div className="admin-loading">
        <div className="loading-spinner"></div>
        <p>Indlæser...</p>
      </div>
    );
  }

  return (
    <div className="admin-container">
      <header className="admin-header">
        <div className="admin-logo">
          <img 
            src="/media/logo/psykologiskperspektiv-logo.avif" 
            alt="Psykologisk Perspektiv" 
            onClick={() => navigate('/admin')}
            style={{ cursor: 'pointer' }}
          />
        </div>
        <div className="admin-header-right">
          <div className="admin-user">
            <span>Logget ind som: </span>
            <strong>{user.email}</strong>
          </div>
          <button onClick={() => navigate('/admin')} className="back-button">
            ← Tilbage
          </button>
        </div>
      </header>
      
      <main className="admin-content">
        <div className="admin-nyheder-container">
          <button className="back-button" onClick={goBack}>
            ← Tilbage til Admin
          </button>
          
          <h1>Administrer Nyheder</h1>
          
          {message.text && (
            <div className={`admin-message ${message.type}`}>
              <p>{message.text}</p>
              <button 
                className="close-message" 
                onClick={closeMessage}
              >
                ×
              </button>
            </div>
          )}
          
          {!isEditing && !isCreating && (
            <div className="admin-section">
              <div className="admin-section-header">
                <h2>Nyheder</h2>
                <button 
                  className="create-btn"
                  onClick={handleCreateNyhed}
                >
                  + Opret ny nyhed
                </button>
              </div>
              
              <p className="admin-description">
                Her kan du administrere nyhederne på forsiden. Du kan oprette, redigere og slette nyheder.
                <strong> Bemærk: Kun de 4 nyeste nyheder vises på forsiden, sorteret efter oprettelsestidspunkt.</strong>
              </p>
              
              {nyheder.length === 0 ? (
                <div className="no-items">
                  <p>Der er ingen nyheder at vise. Opret en ny nyhed for at komme i gang.</p>
                </div>
              ) : (
                <div className="nyheder-list">
                  {nyheder.map((nyhed, index) => (
                    <div key={nyhed.id} className={`nyhed-item ${index < 4 ? 'on-frontpage' : 'not-on-frontpage'}`}>
                      {index < 4 && (
                        <div className="frontpage-badge">
                          <span className="badge-text">Vises på forsiden</span>
                        </div>
                      )}
                      <div className="nyhed-preview">
                        {nyhed.imageUrl && (
                          <div className="nyhed-image">
                            <img src={nyhed.imageUrl} alt={nyhed.title} />
                          </div>
                        )}
                        <div className="nyhed-content">
                          <h3>{nyhed.title}</h3>
                          <p>{nyhed.content.length > 100 
                            ? `${nyhed.content.substring(0, 100)}...` 
                            : nyhed.content}</p>
                          <div className="nyhed-button-info">
                            <span>Knap: "{nyhed.buttonText}" → {nyhed.buttonLink}</span>
                          </div>
                        </div>
                      </div>
                      <div className="nyhed-actions">
                        <button 
                          className="edit-btn"
                          onClick={() => handleEditNyhed(nyhed)}
                        >
                          Rediger
                        </button>
                        <button 
                          className="delete-btn"
                          onClick={() => handleDeleteNyhed(nyhed.id)}
                        >
                          Slet
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
          
          {(isEditing || isCreating) && (
            <div className="admin-section">
              <h2>{isCreating ? 'Opret ny nyhed' : 'Rediger nyhed'}</h2>
              
              <div className="nyhed-form">
                <div className="form-group">
                  <label htmlFor="title">Overskrift</label>
                  <input
                    type="text"
                    id="title"
                    name="title"
                    value={editedNyhed.title}
                    onChange={handleInputChange}
                    placeholder="Skriv en fængende overskrift"
                  />
                </div>
                
                <div className="form-group">
                  <label htmlFor="content">Indhold</label>
                  <FormatBar 
                    activeFormats={activeFormats}
                    onFormatClick={handleFormatClick}
                  />
                  <div
                    ref={editorRef}
                    className="rich-text-editor"
                    contentEditable
                    onKeyDown={handleKeyDown}
                    onFocus={handleEditorFocus}
                    data-placeholder="Skriv nyhedens indhold her"
                  />
                </div>
                
                <div className="form-row">
                  <div className="form-group">
                    <label htmlFor="buttonText">Knap-tekst</label>
                    <input
                      type="text"
                      id="buttonText"
                      name="buttonText"
                      value={editedNyhed.buttonText}
                      onChange={handleInputChange}
                      placeholder="F.eks. 'Læs mere'"
                    />
                  </div>
                  
                  <div className="form-group">
                    <label htmlFor="buttonLink">Knap-destination (link)</label>
                    <input
                      type="text"
                      id="buttonLink"
                      name="buttonLink"
                      value={editedNyhed.buttonLink}
                      onChange={handleInputChange}
                      placeholder="F.eks. '/supervision'"
                    />
                  </div>
                </div>
                
                <div className="form-group">
                  <label>Billede</label>
                  <div className="image-uploader">
                    {preview && (
                      <div className="image-preview">
                        <img src={preview} alt="Forhåndsvisning" />
                      </div>
                    )}
                    
                    <label className="upload-btn">
                      {preview ? 'Skift billede' : 'Vælg billede'}
                      <input 
                        type="file" 
                        accept="image/*" 
                        onChange={handleFileChange}
                        disabled={uploading}
                      />
                    </label>
                  </div>
                </div>
                
                <div className="form-actions">
                  <button 
                    className="save-btn"
                    onClick={handleSaveNyhed}
                    disabled={uploading || !editedNyhed.title}
                  >
                    {uploading ? 'Gemmer...' : 'Gem nyhed'}
                  </button>
                  <button 
                    className="cancel-btn"
                    onClick={handleCancel}
                    disabled={uploading}
                  >
                    Annuller
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      </main>
      
      <footer className="admin-footer">
        <p>&copy; {new Date().getFullYear()} Psykologisk Perspektiv - Admin Panel</p>
      </footer>
    </div>
  );
};

export default AdminNyheder;
